<template>
  <div class="space-y-3">
    <app-alert
      v-if="showValidationError && !selectedStatement"
      color="error"
      icon="info-circle"
      heading="Please select a eligible statement"
      class="mt-6"
    >
      Make sure you selected or created a statement to defer the reservation.
    </app-alert>

    <div class="space-y-1">
      <p class="label is-required">Eligible statements</p>
      <p class="text-xs text-gray-600">
        Move the selected reservation to a statement that uses the same funding source.
      </p>
    </div>

    <div class="flex gap-1 pt-2 pl-2">
      <app-badge size="sm">{{ statementSupplierName }}</app-badge>
      <app-badge size="sm">
        <template #prefixIcon>
          <country-flag
            :country-code="statementFundingSource?.country_code"
            class="w-4 h-4"
          />
        </template>
        {{ statementFundingSourceLabel }}
      </app-badge>
      <statement-status-badge
        :status="StatementStatus.OPENED"
        size="sm"
      />
    </div>

    <div class="space-y-2">
      <template v-if="isLoading">
        <div class="skeleton h-9 rounded-lg" />
        <div class="skeleton h-9 rounded-lg" />
        <div class="skeleton h-9 rounded-lg" />
      </template>

      <template v-else>
        <label
          v-for="statement in statements"
          :key="statement.id"
          class="flex items-center gap-2 cursor-pointer text-sm ring ring-gray-200 rounded-lg px-4 py-2.5 has-checked:ring-2 has-checked:ring-primary"
        >
          <input
            type="radio"
            name="statement_id"
            class="radio radio-primary radio-xs"
            :value="statement"
            v-model="selectedStatement"
          />
          <span class="font-semibold">{{ statement.public_id }}</span>
          <span class="ml-auto text-gray-600">
            {{ formatDate(statement.start_date) }} - {{ formatDate(statement.end_date) }}
          </span>
        </label>
      </template>

      <div
        v-if="!isLoading"
        class="flex items-center justify-between px-4 py-6 border border-dashed border-gray-400 rounded-lg"
        :class="{ 'h-31': !statements.length }"
      >
        <span class="text-sm text-gray-600">{{ newStatementLabel }}</span>
        <button
          class="btn btn-link"
          @click="emit('click:new-statement')"
        >
          <app-icon
            name="flip-forward"
            size="sm"
          />
          Create and defer to a new statement
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch, inject } from 'vue';
import { formatDate } from '@/utils/dates';
import { StatementStatus } from '@/constants/statements';
import { FundingSourceTransferMethodLabels } from '@/constants/funding-sources.js';
import StatementStatusBadge from '@/components/statements/StatementStatusBadge.vue';

const props = defineProps({
  statements: {
    type: Array,
    required: true,
    default: () => [],
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['click:new-statement', 'update:selected-statement-id']);

const statement = inject('statement');
const selectedStatement = ref(null);
const showValidationError = ref(false);

const statementSupplierName = computed(() => statement?.value?.suppliers[0]?.name);
const statementFundingSource = computed(() => statement?.value?.funding_source || {});
const fundingSourceTransferMethod = computed(
  () => FundingSourceTransferMethodLabels[statementFundingSource.value?.transfer_method]
);
const statementFundingSourceLabel = computed(
  () => `${statementFundingSource.value?.currency_alpha_code} - ${fundingSourceTransferMethod.value}`
);

const newStatementLabel = computed(() => {
  return !props.statements.length ? "There aren't eligible statements" : 'None of the above works for you?';
});

const triggerValidationError = () => {
  showValidationError.value = true;
};

watch(selectedStatement, (value) => {
  showValidationError.value = !value;
});

defineExpose({
  selectedStatement,
  triggerValidationError,
});
</script>
